import React, { useState, useEffect, useRef } from 'react';

const InteractiveTooltip: React.FC<{ explanation: string; children: React.ReactNode; className?: string }> = ({ explanation, children, className }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState<'top' | 'bottom'>('top');
    const timeoutRef = useRef<number | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleInteraction = () => {
        if (!wrapperRef.current) return;

        // Clear any existing timer to avoid premature hiding
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        // --- Smart Position Calculation ---
        const rect = wrapperRef.current.getBoundingClientRect();
        // Check if there's enough space above the element. 100px is a safe estimate for tooltip height + margins.
        if (rect.top < 100) { 
            setPosition('bottom');
        } else {
            setPosition('top');
        }
        
        setIsVisible(true);

        // For touch-enabled devices, automatically hide the tooltip after 3 seconds
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            timeoutRef.current = window.setTimeout(() => {
                setIsVisible(false);
            }, 3000);
        }
    };

    const handleMouseLeave = () => {
        // Clear timer on mouse leave to prevent it from hiding during hover
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsVisible(false);
    };
    
    // Cleanup timer on component unmount
    useEffect(() => () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }, []);

    return (
        <div 
            ref={wrapperRef}
            className={`relative cursor-pointer ${className || ''}`}
            onMouseEnter={handleInteraction}
            onMouseLeave={handleMouseLeave}
            onClick={handleInteraction} // Use the same handler for hover and click
        >
            {children}
            <div className={`
                absolute left-1/2 -translate-x-1/2 w-max max-w-[200px] 
                bg-slate-800/95 text-white text-xs rounded-md py-1.5 px-3 z-30 
                backdrop-blur-sm border border-slate-600 shadow-lg 
                transition-all duration-300 ease-out
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 pointer-events-none'}
                ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
                ${isVisible ? '' : (position === 'top' ? 'translate-y-1' : '-translate-y-1')}
            `}>
                <div className={`
                    absolute left-1/2 -translate-x-1/2 w-0 h-0
                    border-x-4 border-x-transparent
                    ${position === 'top' ? 'top-full border-t-4 border-t-slate-800/95' : 'bottom-full border-b-4 border-b-slate-800/95'}
                `}></div>
                {explanation}
            </div>
        </div>
    );
};

export default InteractiveTooltip;
